<template>
  <div class="atb-py-6">
    <Card class="card-size atb-ml-20 atb-pt-6">
      <template #body>
        <Form>
          <div
            class="atb-grid atb-grid-cols-2 atb-gap-y-4 atb-gap-x-14 atb-px-2 atb-py-4"
          >
            <FormField
              :label="$t('mdsFieldLongName')"
              :required="isNew ? 'true' : 'false'"
              form-field-class="input-field-width"
              :message="errors.name"
              :message-type="'error'"
            >
              <Textbox
                id="mdsLongName"
                v-model.trim="form.name"
                type="text"
                :maxlength="MaxValue.MAX_SHORT_NAME"
                :disabled="!isNew || readOnly"
                :state="errors.name ? 'error' : 'basic'"
                @update:modelValue="errors.name = ''"
              />
            </FormField>
            <FormField
              :label="$t('mdsFieldShortName')"
              required="true"
              form-field-class="input-field-width"
              :message="errors.acronym"
              :message-type="'error'"
            >
              <Textbox
                id="mdsShortName"
                v-model.trim="form.acronym"
                type="text"
                :maxlength="MaxValue.MAX_SHORT_NAME"
                :disabled="readOnly"
                :state="errors.acronym ? 'error' : 'basic'"
                @update:modelValue="errors.acronym = ''"
              />
            </FormField>
          </div>
          <div class="atb-px-2 atb-py-4">
            <FormField
              :label="$t('mdsFieldDescription')"
              form-field-class="input-field-width"
            >
              <Textarea
                id="mdsDescription"
                v-model="form.description"
                field-control-class="atb-h-24"
                :disabled="readOnly"
              />
            </FormField>
          </div>
        </Form>
        <div class="datasources-table atb-m-4">
          <div class="atb-font-bold atb-px-4 atb-py-4">
            {{ $t('mdsFieldSources') }}
            <span class="atb-text-red-400">*</span>
          </div>
          <div class="grid-top-border">
            <AgGridVue
              id="mdsSourcesGrid"
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="sourceColumnDefs"
              :row-data="sourceRowData"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :row-multi-select-with-click="true"
              :overlay-no-rows-template="noSourcesTemplate"
              @grid-ready="onSourcesGridReady"
            />
          </div>
          <p
            v-if="errors.members"
            id="mdsSourcesError"
            class="atb-px-4 atb-py-2 atb-text-red-400"
          >
            {{ errors.members }}
          </p>
        </div>
        <div class="atb-flex atb-flex-row atb-justify-end atb-gap-x-8 atb-pt-8">
          <Button
            v-if="!isNew"
            id="mdsDeleteButton"
            motif="basic"
            :label="$t('mdsDelete')"
            :title="$t('mdsDelete')"
            :disabled="readOnly || saving"
            @click="askDelete"
          />
          <Button
            id="mdsSaveButton"
            motif="primary"
            :label="$t('mdsSave')"
            :title="$t('mdsSave')"
            :disabled="readOnly || saving"
            @click="save"
          />
        </div>
      </template>
    </Card>
    <Modal
      :show="isDeleteConfirmationDisplayed"
      container-class="atb-w-96"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      @onModalClose="isDeleteConfirmationDisplayed = false"
    >
      <template #header>{{ $t('mdsDelete') }}</template>
      <template #body>
        <div id="mdsDeleteConfirmationModal">
          {{ $t('mdsQuestionDelete') }}
        </div>
      </template>
      <template #footer>
        <Button
          motif="basic"
          @click="isDeleteConfirmationDisplayed = false"
          >{{ $t('mdsCancel') }}</Button
        >
        <Button
          id="mdsDeleteConfirmButton"
          @click="confirmDelete"
          >{{ $t('mdsOk') }}</Button
        >
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onMounted,
    reactive,
    Ref,
    ref,
    toRefs,
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { GridApi, GridReadyEvent } from 'ag-grid-community';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Form from '@atb-ui/eds-vue/src/components/Form/Form.vue';
  import FormField from '@atb-ui/eds-vue/src/components/Form/FormField.vue';
  import Textbox from '@atb-ui/eds-vue/src/components/Textbox.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import { MaxValue } from '@/app/shared/enums/max-value.enum';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import { MultiDatasourceRequest } from '@/app/shared/types/multi-datasource-request.interface';
  import { ENTITY_ALPHANUMERIC_REGEX } from '@/app/utils/regexpr-const';
  import multiDatasourceApi, {
    refreshSelectedClient,
  } from '@/app/features/ams/tools/mds/multi-datasource-service';
  import { errorMessage } from '@/app/features/ams/tools/mds/mds-error';

  /**
   * One multi datasource, opened in its own tab as in 5.x: Long Name (fixed once created),
   * Short Name, Description and the client's data sources it combines. A copy starts as a
   * new one with the description and data sources of copyFrom; the Short Name is left empty
   * because admin requires it to be unique among multi datasources.
   */
  interface MdsDetailsEvents {
    (_event: 'saved', _datasource: Datasource): void;
    (_event: 'deleted', _datasource: Datasource): void;
  }
  interface MdsDetailsProps {
    datasource: Datasource | null;
    copyFrom?: Datasource | null;
    readOnly: boolean;
  }

  const props = withDefaults(defineProps<MdsDetailsProps>(), {
    copyFrom: null,
  });
  const emit = defineEmits<MdsDetailsEvents>();
  const { datasource, copyFrom } = toRefs(props);
  const { t } = useI18n();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();
  const gridApi = ref(null) as Ref<GridApi | null>;
  const memberIds = ref<Array<number>>([]);
  const saving = ref(false);
  const savedId = ref<number | null>(datasource.value?.id ?? null);
  const isNew = computed(() => savedId.value === null);

  const form = reactive({
    name: datasource.value?.name || '',
    acronym: datasource.value?.acronym || '',
    description:
      datasource.value?.description || copyFrom.value?.description || '',
  });
  const errors = reactive({ name: '', acronym: '', members: '' });

  const sourceRowData = computed<Array<Datasource>>(() =>
    (clientStore.selectedClient?.dataSources || [])
      .filter((ds: Datasource) => !ds.isMulti)
      .sort((a: Datasource, b: Datasource) => a.name.localeCompare(b.name))
  );

  const sourceColumnDefs = [
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('mdsColumnLongName'),
      suppressSizeToFit: true,
      sortable: true,
    },
    {
      field: 'description',
      headerName: t('mdsColumnDescription'),
    },
  ];

  const noSourcesTemplate = `<span>${t('mdsNoSources')}</span>`;

  const selectMembers = () => {
    gridApi.value?.forEachNode((node) => {
      node.setSelected(memberIds.value.includes(node.data.id));
    });
  };

  const onSourcesGridReady = async (params: GridReadyEvent) => {
    gridApi.value = params.api;
    await nextTick();
    selectMembers();
    gridApi.value.sizeColumnsToFit();
  };

  const isDeleteConfirmationDisplayed = ref(false);

  onMounted(async () => {
    const membersOf = savedId.value ?? copyFrom.value?.id ?? null;
    if (membersOf === null) {
      return;
    }
    try {
      const {
        data: { data },
      } = await multiDatasourceApi.getMultiDatasourceMembers(membersOf);
      memberIds.value = (data || []).map((member) => member.id);
      selectMembers();
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsPage'),
        Motif.ERROR
      );
    }
  });

  const selectedIds = (): Array<number> =>
    (gridApi.value?.getSelectedRows() || []).map((ds: Datasource) => ds.id);

  const validate = (): boolean => {
    errors.name = '';
    errors.acronym = '';
    errors.members = '';
    if (isNew.value) {
      const taken = (clientStore.selectedClient?.dataSources || []).some(
        (ds: Datasource) => ds.name.toUpperCase() === form.name.toUpperCase()
      );
      if (!ENTITY_ALPHANUMERIC_REGEX.test(form.name)) {
        errors.name = t('mdsNameInvalid');
      } else if (taken) {
        errors.name = t('mdsDuplicateName');
      }
    }
    if (!form.acronym) {
      errors.acronym = t('mdsShortNameRequired');
    }
    if (selectedIds().length === 0) {
      errors.members = t('mdsNoDsSelected');
    }
    return !errors.name && !errors.acronym && !errors.members;
  };

  const save = async () => {
    if (!validate()) {
      return;
    }
    saving.value = true;
    const request: MultiDatasourceRequest = {
      acronym: form.acronym,
      description: form.description.trim(),
      subDataSourceIds: selectedIds(),
      clientId: clientStore.selectedClient.id,
    };
    try {
      let response;
      if (savedId.value === null) {
        request.name = form.name;
        response = await multiDatasourceApi.createMultiDatasource(request);
      } else {
        response = await multiDatasourceApi.updateMultiDatasource(
          savedId.value,
          request
        );
      }
      const saved = response.data.data;
      savedId.value = saved.id;
      memberIds.value = request.subDataSourceIds || [];
      await refreshSelectedClient();
      alertStore.showNotification(
        t('mdsConfirmSave'),
        t('mdsPage'),
        Motif.SUCCESS
      );
      emit('saved', saved);
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsPage'),
        Motif.ERROR
      );
    } finally {
      saving.value = false;
    }
  };

  // 5.x warned about coded filters and deleted anyway; 6.x filters would lose their
  // datasource, so a multi datasource in use cannot be deleted (admin answers 409 too).
  const askDelete = async () => {
    if (savedId.value === null) {
      return;
    }
    try {
      const {
        data: { data },
      } = await multiDatasourceApi.getMultiDatasourceUsage(savedId.value);
      const count = data?.versionControls || 0;
      if (count > 0) {
        alertStore.showNotification(
          t('mdsHasFilters', { count }),
          t('mdsPage'),
          Motif.ERROR
        );
        return;
      }
      isDeleteConfirmationDisplayed.value = true;
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsPage'),
        Motif.ERROR
      );
    }
  };

  const confirmDelete = async () => {
    isDeleteConfirmationDisplayed.value = false;
    if (savedId.value === null) {
      return;
    }
    saving.value = true;
    try {
      await multiDatasourceApi.deleteMultiDatasource(savedId.value);
      await refreshSelectedClient();
      alertStore.showNotification(
        t('mdsConfirmDelete'),
        t('mdsPage'),
        Motif.SUCCESS
      );
      emit('deleted', {
        ...(datasource.value as Datasource),
        id: savedId.value,
        name: form.name,
      });
    } catch (e) {
      alertStore.showNotification(
        errorMessage(e, t),
        t('mdsPage'),
        Motif.ERROR
      );
    } finally {
      saving.value = false;
    }
  };
</script>

<style scoped>
  .card-size {
    width: 70%;
  }
  .input-field-width {
    width: 90%;
  }
  .datasources-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }
  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }
</style>
